/**
 * @name OperadorControl
 * @description Componente de controle do operador na interface do totem
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/components/common/OperadorControl.tsx
 */

import { StyledTextField } from "@/components/common/fields";
import { useOperadorContext } from "@/contexts/OperadorContext";
import { useFullscreen } from "@/hooks";
import useThemeContext from "@/themes/hooks/useThemeContext";
import {
  Lock as LockIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DualPrinterConfigModal from "../modals/DualPrinterConfigModal";
import QZTrayAuthModal from "../modals/QZTrayAuthModal";

interface OperadorControlProps {
  showFullBar?: boolean; // Se deve mostrar a barra completa ou apenas o controle
}

const OperadorControl: React.FC<OperadorControlProps> = ({
  showFullBar = true,
}) => {
  const { theme } = useThemeContext();
  const { operadorAtual, encerrarSessao, validarSenhaOperador } =
    useOperadorContext();
  const { unlockAndExitFullscreen } = useFullscreen();

  const [tempoSessao, setTempoSessao] = useState<string>("");
  const [dialogLogout, setDialogLogout] = useState(false);
  const [senhaConfirmacao, setSenhaConfirmacao] = useState<string>("");
  const [senhaError, setSenhaError] = useState<string>("");

  // Estados para QZ Tray
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Atualizar tempo de sessão
  useEffect(() => {
    if (!operadorAtual?.sessao_inicio) return;

    const interval = setInterval(() => {
      const agora = new Date();
      const inicio = new Date(operadorAtual.sessao_inicio);
      const diff = Math.floor((agora.getTime() - inicio.getTime()) / 1000);

      const horas = Math.floor(diff / 3600);
      const minutos = Math.floor((diff % 3600) / 60);

      setTempoSessao(
        `${horas.toString().padStart(2, "0")}:${minutos.toString().padStart(2, "0")}`,
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [operadorAtual?.sessao_inicio]);

  const handleConfirmarLogout = async () => {
    if (!senhaConfirmacao.trim()) {
      setSenhaError("Senha é obrigatória");
      return;
    }

    try {
      const senhaValida = await validarSenhaOperador(senhaConfirmacao);

      if (senhaValida) {
        // Primeiro destrava e sai da tela cheia
        console.log("🔓 Destravando tela cheia antes de encerrar sessão...");
        await unlockAndExitFullscreen();

        // Depois encerra a sessão
        await encerrarSessao();
        setDialogLogout(false);
        setSenhaConfirmacao("");
        setSenhaError("");
      } else {
        setSenhaError("Senha incorreta");
      }
    } catch (error) {
      setSenhaError("Erro ao validar senha");
    }
  };

  const handleCancelarLogout = () => {
    setDialogLogout(false);
    setSenhaConfirmacao("");
    setSenhaError("");
  };

  /**
   * Abre modal de autenticação para configurações
   */
  const handleOpenSettings = () => {
    setShowAuthModal(true);
  };

  /**
   * Callback quando autenticação é bem-sucedida
   */
  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setShowConfigModal(true);
  };

  /**
   * Fecha modal de configuração
   */
  const handleCloseConfig = () => {
    setShowConfigModal(false);
  };

  if (!operadorAtual) return null;

  // Versão compacta (só um chip com info do operador)
  if (!showFullBar) {
    return (
      <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}>
        <Chip
          icon={<PersonIcon />}
          label={`${operadorAtual.nome} • ${tempoSessao}`}
          variant="filled"
          color="primary"
          sx={{
            bgcolor: theme.palette.primary.main,
            color: "white",
            fontWeight: "bold",
            "& .MuiChip-icon": { color: "white" },
          }}
          onClick={() => setDialogLogout(true)}
        />
      </Box>
    );
  }

  // Versão completa (AppBar)
  return (
    <>
      <AppBar
        position="static"
        elevation={2}
        sx={{
          bgcolor: theme.palette.primary.dark,
          borderBottom: `2px solid ${theme.palette.primary.main}`,
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LockIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" fontWeight="bold">
              TOTEM LIBERADO
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontWeight="medium">
                {operadorAtual.nome}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TimeIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontFamily="monospace">
                {tempoSessao}
              </Typography>
            </Box>

            <IconButton
              color="inherit"
              size="small"
              onClick={handleOpenSettings}
              title="Configurar Impressoras (Totem + Cozinha)"
              sx={{
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                ml: 1,
              }}
            >
              <SettingsIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <IconButton
              color="inherit"
              size="small"
              onClick={() => setDialogLogout(true)}
              title="Encerrar Sessão"
              sx={{
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                ml: 0.5,
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Dialog de Confirmação de Logout com Senha */}
      <Dialog
        open={dialogLogout}
        onClose={handleCancelarLogout}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <LockIcon
            sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 1 }}
          />
          <Typography variant="h6" fontWeight="bold">
            Confirmar Encerramento
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Para encerrar a sessão e bloquear o totem, confirme sua senha:
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Tempo de sessão atual: <strong>{tempoSessao}</strong>
          </Typography>

          <StyledTextField
            type="password"
            label="Senha do Operador"
            value={senhaConfirmacao}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSenhaConfirmacao(e.target.value);
              if (senhaError) setSenhaError("");
            }}
            error={!!senhaError}
            helperText={senhaError}
            fullWidth
            autoFocus
            withGlassContainer={false}
            marginBottom={2}
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                handleConfirmarLogout();
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleCancelarLogout}
            sx={{ minWidth: 100 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmarLogout}
            disabled={!senhaConfirmacao.trim()}
            sx={{ minWidth: 100 }}
          >
            Encerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modais de Configuração de Impressoras */}
      <QZTrayAuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />

      <DualPrinterConfigModal
        open={showConfigModal}
        onClose={handleCloseConfig}
      />
    </>
  );
};

export default OperadorControl;
